import React, { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile widget with graceful fallback.
 *
 * Behavior:
 *   - If REACT_APP_TURNSTILE_SITE_KEY is unset → render nothing, parent
 *     submits with no token.
 *   - Otherwise → load the Turnstile script once, render the widget, call
 *     onToken with the resolved token (or null on expiry/error).
 *
 * Theme is neutral so it works on light + dark hero backgrounds.
 */
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise = null;
function loadScript() {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.turnstile) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export default function Turnstile({ onToken, theme = "light" }) {
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const ref = useRef(null);
  const widgetIdRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    loadScript().then((ok) => {
      if (!ok || cancelled) return;
      // Wait for window.turnstile to be ready
      const tryRender = () => {
        if (cancelled) return;
        if (!window.turnstile || !ref.current) {
          setTimeout(tryRender, 50);
          return;
        }
        widgetIdRef.current = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => onToken && onToken(token),
          "expired-callback": () => onToken && onToken(null),
          "error-callback": () => onToken && onToken(null),
        });
        setLoaded(true);
      };
      tryRender();
    });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {
          /* noop */
        }
      }
    };
  }, [siteKey, theme, onToken]);

  if (!siteKey) return null;
  return (
    <div className="mt-4" data-testid="turnstile-widget">
      <div ref={ref} />
      {!loaded ? (
        <p className="mt-1 text-xs text-ridge-muted">Loading spam check…</p>
      ) : null}
    </div>
  );
}
